import { useEffect, useRef, type ReactNode } from "react";
import {
  IconActivity,
  IconCalendar,
  IconCamera,
  IconClipboard,
  IconFolder,
  IconHeart,
  IconSignature,
} from "../_shared/icons";
import { navigate } from "../_shared/router";
import type { Route } from "../_shared/router";
import { useDemo } from "../_shared/demo.provider";
import { formatRelativeFr } from "../_shared/date-fmt";
import { children } from "../_data/mock";

const childPill = {
  orange: "bg-[var(--child-1-light)] text-[var(--child-1-primary)]",
  blue: "bg-[var(--child-2-light)] text-[var(--child-2-primary)]",
} as const;

type TodoKind =
  | "rdv"
  | "document"
  | "signature"
  | "questionnaire"
  | "photo"
  | "exercise"
  | "testimonial";

type TodoItem = {
  id: string;
  kind: TodoKind;
  action: string;
  detail: string;
  childId?: string;
  icon: ReactNode;
  route: Route["name"];
  createdAt?: string;
};

export function TodoCard() {
  const {
    appointmentRequests,
    documents,
    signatures,
    questionnaires,
    photoSessions,
    exercises,
    testimonials,
  } = useDemo();

  const items: TodoItem[] = [
    ...appointmentRequests.map<TodoItem>((r) => ({
      id: `rdv-${r.id}`,
      kind: "rdv",
      action: "Prendre rendez-vous",
      detail: `${r.reason} — ${r.requestedBy}`,
      childId: r.childId,
      icon: <IconCalendar width={22} height={22} />,
      route: "book",
      createdAt: r.createdAt,
    })),
    ...documents
      .filter((d) => d.status === "missing")
      .map<TodoItem>((d) => ({
        id: `doc-${d.id}`,
        kind: "document",
        action: "Envoyer document",
        detail: `${d.label} — ${d.detail}`,
        childId: d.childId,
        icon: <IconFolder width={22} height={22} />,
        route: "documents",
      })),
    ...signatures.map<TodoItem>((s) => ({
      id: `sig-${s.id}`,
      kind: "signature",
      action: "Signer document",
      detail: s.label,
      childId: s.childId,
      icon: <IconSignature width={22} height={22} />,
      route: "signatures",
      createdAt: s.createdAt,
    })),
    ...questionnaires.map<TodoItem>((q) => ({
      id: `quest-${q.id}`,
      kind: "questionnaire",
      action: "Remplir questionnaire",
      detail: q.label,
      childId: q.childId,
      icon: <IconClipboard width={22} height={22} />,
      route: "summary",
      createdAt: q.createdAt,
    })),
    ...photoSessions
      .filter((p) => p.status !== "done")
      .map<TodoItem>((p) => ({
        id: `photo-${p.id}`,
        kind: "photo",
        action: "Séance photo",
        detail: `${p.label} — avant le ${p.dueDate}`,
        childId: p.childId,
        icon: <IconCamera width={22} height={22} />,
        route: "photo-sessions",
      })),
    ...exercises
      .filter((ex) => !ex.doneToday)
      .map<TodoItem>((ex) => ({
        id: `ex-${ex.id}`,
        kind: "exercise",
        action: "Faire un exercice",
        detail: ex.label,
        childId: ex.childId,
        icon: <IconActivity width={22} height={22} />,
        route: "exercises",
      })),
    ...testimonials.map<TodoItem>((t) => ({
      id: `temoin-${t.id}`,
      kind: "testimonial",
      action: "Laisser un témoignage",
      detail: t.prompt,
      childId: t.childId,
      icon: <IconHeart width={22} height={22} />,
      route: "summary",
      createdAt: t.createdAt,
    })),
  ];

  const scrollRef = useRef<HTMLUListElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (el.scrollWidth <= el.clientWidth) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="relative -mx-4 border-y border-[var(--sf-primary)]/25 px-4 pb-3 pt-4 sm:-mx-5 sm:px-5 lg:-mx-10 lg:px-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, var(--sf-primary-100) 0%, color-mix(in srgb, var(--sf-primary-100) 35%, transparent) 45%, transparent 90%)",
        }}
      />
      <header className="relative mb-2.5 flex items-center gap-2 px-1">
        <h2 className="text-[19px] font-extrabold tracking-tight text-[var(--text-1)] sm:text-[20px]">
          Éléments à traiter
        </h2>
        <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[var(--sf-primary)] px-1.5 text-[12.5px] font-bold text-white">
          {items.length}
        </span>
      </header>
      <ul
        ref={scrollRef}
        className="relative -mx-4 flex gap-3 overflow-x-auto px-4 pb-2 sm:-mx-5 sm:px-5 lg:-mx-10 lg:px-10"
        style={{ scrollbarGutter: "stable" }}
      >
        {items.map((item) => {
          const child = item.childId
            ? children.find((c) => c.id === item.childId)
            : undefined;
          return (
            <li key={item.id} className="shrink-0">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    item.route,
                    item.childId ? { child: item.childId } : undefined,
                  )
                }
                className="flex h-full w-64 items-start gap-3 rounded-2xl bg-[var(--surface-1)] p-3.5 text-left shadow-[0_1px_3px_rgba(15,23,42,0.04),0_1px_2px_rgba(15,23,42,0.06)] ring-1 ring-[var(--border-1)] transition active:scale-[0.99] hover:brightness-[0.98] dark:hover:brightness-110"
              >
                <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-[var(--sf-primary-100)] text-[var(--sf-primary)]">
                  {item.icon}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[15px] font-bold leading-snug tracking-tight text-[var(--text-1)]">
                      {item.action}
                    </span>
                    {item.createdAt ? (
                      <span className="shrink-0 text-[10.5px] font-medium text-[var(--text-3)]">
                        {formatRelativeFr(item.createdAt)}
                      </span>
                    ) : null}
                  </div>
                  <span className="line-clamp-2 text-[13px] leading-snug text-[var(--text-2)]">
                    {item.detail}
                  </span>
                  {child ? (
                    <span
                      className={`mt-1 inline-flex h-5.5 w-fit items-center rounded-full px-2 text-[11.5px] font-semibold ${childPill[child.color]}`}
                    >
                      {child.firstName}
                    </span>
                  ) : null}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
